import { realtimeService } from '@peritasai/data-access';
import {
  RealtimeEventType,
} from '@peritasai/types';
import type {
  AssetUpdateEventFilters,
  EventSubscription,
  RealtimeEvent,
} from '@peritasai/types';
import { useEffect, useMemo, useRef, useState } from 'react';

export interface UseRealtimeAssetUpdatesOptions {
  filters?: AssetUpdateEventFilters;
  eventTypes?: RealtimeEventType[];
  onEvent?: (event: RealtimeEvent) => void;
  enabled?: boolean;
}

export interface UseRealtimeAssetUpdatesResult {
  connected: boolean;
  error: Error | null;
}

/**
 * React hook for subscribing to real-time asset updates via SSE.
 */
export function useRealtimeAssetUpdates(
  options: UseRealtimeAssetUpdatesOptions = {}
): UseRealtimeAssetUpdatesResult {
  const {
    filters = {},
    eventTypes = [RealtimeEventType.ASSET_STATUS_UPDATED, RealtimeEventType.ASSET_LOCATION_UPDATED],
    onEvent,
    enabled = true,
  } = options;

  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const onEventRef = useRef(onEvent);

  const RECONNECT_DELAY_MS = 100;

  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  const eventTypesKey = useMemo(() => JSON.stringify(eventTypes), [eventTypes]);
  const typeKey = useMemo(() => {
    const value = filters.type;
    return Array.isArray(value) ? value.join(',') : value;
  }, [filters.type]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    const subscriptions: EventSubscription[] = eventTypes.map((eventType) => ({
      eventType,
      filters: Object.keys(filters).length > 0 ? filters : undefined,
    }));

    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectTimeoutRef.current = null;
      const controller = new AbortController();
      controllerRef.current = controller;

      const connect = async () => {
        try {
          await realtimeService.connect({
            subscriptions,
            signal: controller.signal,
            onEvent: (event) => {
              onEventRef.current?.(event);
            },
            onStateChange: (state) => {
              setConnected(state.connected);
              setError(state.error);
            },
          });
        } catch (err) {
          if (err instanceof Error && err.name !== 'AbortError') {
            setError(err);
            setConnected(false);
          }
        }
      };

      void connect();
    }, RECONNECT_DELAY_MS);

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (controllerRef.current) {
        controllerRef.current.abort();
        controllerRef.current = null;
      }
      setConnected(false);
      setError(null);
    };
  }, [
    enabled,
    filters.assetCatalogId,
    filters.locationId,
    filters.name,
    filters.status,
    typeKey,
    eventTypesKey,
  ]);

  return { connected, error };
}
